// ════════════════════════════════════════════════════════════════════════════
//  useChatV2.js  ·  NSL Chat Hub V2 — React Hooks
//  Sin dependencias externas. Fetch nativo + polling por setInterval.
//  Namespaced: todos los exports tienen sufijo V2.
//
//  ═══════════════════════════════════════════════════════════════════════════
//  AUDITORÍA DE SEGURIDAD 2026-05-11 — FIXES APLICADOS:
//
//  [FIX P-06] useStudentMessagesV2 — lastId migrado de useState → useRef.
//    ANTES: lastId era state → estaba en deps de fetcher → cada mensaje nuevo
//    actualizaba lastId → recreaba fetcher → usePoll reiniciaba el intervalo
//    → el siguiente poll inmediato volvía a actualizar lastId → LOOP INFINITO
//    de recreación de intervalos (memory leak + CPU spike).
//    AHORA: lastIdRef.current se muta sin disparar re-renders. fetcher solo
//    depende de convId — estable entre polls. Sin loop.
//
//  [FIX P-09] useAdminConversationsV2 — expone isOffline tras ≥ 3 fallos
//    consecutivos. El componente debe mostrar un banner al admin informando
//    que la conexión está caída. errorCountRef se resetea a 0 en cada éxito.
//    Usa Ref (no state) para el contador — no fuerza re-renders en cada fallo.
//    Solo setState cuando cruza el umbral OFFLINE_THRESHOLD = 3.
//
//  [FIX ARCH] apiFetch exportado para consumo directo en componentes.
//    Elimina el patrón de fetch() nativo con localStorage.getItem("token")
//    esparcido en los componentes. Un solo punto de inyección de Bearer token.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import { useLatido } from "./useLatido";

// ─────────────────────────────────────────────────────────────────────────────
//  AUTH: lee el Bearer token (mismo patrón que el resto de la app)
// ─────────────────────────────────────────────────────────────────────────────

function getToken() {
    return localStorage.getItem("token") || "";
}

/**
 * apiFetch — cliente HTTP centralizado con inyección de Bearer token.
 *
 * [FIX ARCH] Exportado para uso directo en componentes que necesitan
 * llamadas puntuales (ej. tracking de plantillas Comms, acciones CRM)
 * sin crear un hook completo.
 *
 * Ventajas sobre fetch() nativo esparcido:
 *   - Bearer token siempre inyectado automáticamente
 *   - Content-Type: application/json por defecto
 *   - Errores HTTP siempre lanzan con el detalle del servidor
 *   - Un solo punto para cambiar la estrategia de autenticación
 *
 * @param {string} url        URL relativa o absoluta del endpoint
 * @param {object} options    Opciones de fetch (method, body, signal, etc.)
 * @returns {Promise<any>}    Respuesta JSON parseada
 * @throws {Error}            Con el detalle del servidor o "HTTP {status}"
 */
export async function apiFetch(url, options = {}) {
    const token = getToken();
    const res = await fetch(url, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...options.headers,
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
//  GENERIC POLL HOOK (interno — no exportado)
//  Fetches data immediately then repeats every `intervalMs`.
//  deps: re-runs when any dep changes (like filter/search params).
//
//  NOTA: los hooks que necesitan control fino del estado de error (como
//  useAdminConversationsV2 con isOffline) implementan su propio loop
//  directamente en lugar de usar este helper genérico.
// ─────────────────────────────────────────────────────────────────────────────

function usePoll(fetcher, intervalMs = 4000, deps = []) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const run = useCallback(async () => {
        try {
            const result = await fetcher();
            setData(result);
            setError(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps

    // El mismo latido que el resto del chat: callado con la pestaña oculta y
    // más lento cuando no pasa nada. Este poll genérico lo usan las
    // estadísticas del chat, que cambian cada mucho (19-09-2026).
    const tarea = useCallback(async () => {
        await run();
        return false;   // sin novedad detectable: que afloje tranquilo
    }, [run]);
    const acelera = useLatido(tarea, { base: intervalMs, techo: 60000 });

    return { data, loading, error, refetch: acelera };
}

// ─────────────────────────────────────────────────────────────────────────────
//  ── ADMIN HOOKS ──────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Número de fallos consecutivos antes de declarar isOffline.
 * Con intervalMs=3500: 3 fallos = ~10.5s de downtime detectado.
 */
const OFFLINE_THRESHOLD = 3;

/**
 * useAdminConversationsV2 — lista de conversaciones con polling.
 *
 * [FIX P-09] Expone `isOffline` (true cuando ≥ OFFLINE_THRESHOLD polls
 * consecutivos fallan). El componente DEBE mostrar un banner al admin
 * para que sepa que el servidor no responde.
 *
 * Implementación directa (sin usePoll genérico) para poder:
 *   a) Controlar errorCountRef sin disparar re-renders en cada fallo
 *   b) Usar AbortController para cancelar polls al desmontar
 *   c) Resetear isOffline a false en el primer poll exitoso
 *
 * @param {string} filter  "all" | "unread" | "overdue" | "tokens" | "b2b"
 * @param {string} search  Texto de búsqueda (escapado en el backend)
 * @returns {{ data, loading, isOffline }}
 *   data.conversations → array de conversaciones formateadas
 *   data.total         → total de conversaciones filtradas
 *   data.total_unread  → suma de no leídos del admin
 *   loading            → true solo en la primera carga
 *   isOffline          → true cuando ≥ 3 polls consecutivos fallan
 */
export function useAdminConversationsV2(filter = "all", search = "") {
    const [internalData, setInternalData] = useState({
        conversations: [],
        total: 0,
        total_unread: 0,
    });
    const [loading, setLoading] = useState(true);
    const [isOffline, setIsOffline] = useState(false);

    // [FIX P-09] Ref para el contador — no fuerza re-renders en cada fallo.
    // Solo setState (isOffline=true) al cruzar el umbral.
    const errorCountRef = useRef(0);

    // Lo que se vio la última vez, para saber si esta vuelta trajo algo nuevo.
    const huellaRef = useRef("");

    const loadData = useCallback(async () => {
        try {
            const params = new URLSearchParams({ filter, search });
            const res = await apiFetch(
                `/api/v2/admin/live-chat/conversations?${params.toString()}`,
            );

            // ── Éxito: resetear contador y limpiar banner de offline ───────────
            errorCountRef.current = 0;
            setIsOffline(false);
            setInternalData({
                conversations: res.conversations || [],
                total: res.total || 0,
                total_unread: res.total_unread || 0,
            });
            setLoading(false);

            // ── ¿HUBO NOVEDAD? (19-09-2026) ────────────────────────────────
            // Con esto el latido decide si seguir a 3,5 s o aflojar. La huella
            // es barata: el total sin leer y el último mensaje de cada hilo.
            const huella = JSON.stringify([
                res.total_unread || 0,
                (res.conversations || []).map((c) => [c.id, c.last_message_at || c.updated_at,
                    c.unread_admin]),
            ]);
            const hubo = huella !== huellaRef.current;
            huellaRef.current = huella;
            return hubo;
        } catch (err) {
            if (err.name === "AbortError") return false;

            // ── Fallo: incrementar y evaluar umbral ────────────────────────────
            errorCountRef.current += 1;
            if (errorCountRef.current >= OFFLINE_THRESHOLD) {
                setIsOffline(true);
            }
            console.error("[Chat Hub] Fetch error:", err);
            setLoading(false);
            return false;
        }
    }, [filter, search]);

    // Antes: `setInterval(loadData, 3500)` mientras la pestaña existiera, la
    // mirase alguien o no. Ahora calla con la pestaña oculta y afloja cuando no
    // pasa nada (ver `useLatido`): el mismo panel, sin el goteo a Neon.
    const acelera = useLatido(loadData, { base: 3500, techo: 30000 });

    return { data: internalData, loading, isOffline, refetch: acelera };
}

/**
 * useAdminMessagesV2 — mensajes de una conversación con polling incremental.
 *
 * Usa useRef para lastId (mismo patrón que el fix P-06 del lado alumno).
 * Dos fases:
 *   1. fetchAll: carga completa al abrir/cambiar conversación
 *   2. pollNew:  incrementa desde lastIdRef.current cada 2.5s
 *
 * @param {number|null} convId  ID de la conversación activa
 * @returns {{ messages, loading, refetch }}
 */
export function useAdminMessagesV2(convId) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const lastIdRef = useRef(0);

    const fetchAll = useCallback(async () => {
        if (!convId) return;
        try {
            const res = await apiFetch(
                `/api/v2/admin/live-chat/conversations/${convId}/messages?limit=80`,
            );
            setMessages(res.messages || []);
            if (res.last_id) lastIdRef.current = res.last_id;
        } finally {
            setLoading(false);
        }
    }, [convId]);

    const pollNew = useCallback(async () => {
        if (!convId) return;
        try {
            const res = await apiFetch(
                `/api/v2/admin/live-chat/conversations/${convId}/messages` +
                `?since_id=${lastIdRef.current}&limit=20`,
            );
            if (res.messages?.length > 0) {
                setMessages((prev) => {
                    const ids = new Set(prev.map((m) => m.id));
                    const news = res.messages.filter((m) => !ids.has(m.id));
                    return news.length > 0 ? [...prev, ...news] : prev;
                });
                lastIdRef.current = res.last_id;
                return true;   // hubo mensajes: el latido vuelve al ritmo vivo
            }
        } catch {
            /* errores de poll silenciosos — no romper UX por un fallo puntual */
        }
        return false;
    }, [convId]);

    // Carga completa al montar o cambiar de conversación
    useEffect(() => {
        setMessages([]);
        setLoading(true);
        lastIdRef.current = 0;
        fetchAll();
    }, [convId, fetchAll]);

    // Antes, cada 2,5 s pasara lo que pasara. Ahora: 2,5 s mientras la
    // conversación esté viva, aflojando hasta 20 s cuando no llega nada, y en
    // silencio absoluto si la pestaña no se ve (19-09-2026).
    const acelera = useLatido(pollNew, { base: 2500, techo: 20000, activo: !!convId });

    return { messages, loading, refetch: fetchAll, acelera };
}

/**
 * useCrmContextV2 — radiografía CRM del alumno.
 *
 * Se carga UNA SOLA VEZ al abrir la conversación (no polling).
 * Para refrescar: desmontar y remontar el componente, o cambiar convId.
 *
 * @param {number|null} convId
 * @returns {{ crm, loading }}
 */
export function useCrmContextV2(convId) {
    const [crm, setCrm] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!convId) {
            setCrm(null);
            return;
        }
        setLoading(true);
        apiFetch(`/api/v2/admin/live-chat/conversations/${convId}/crm`)
            .then(setCrm)
            .catch(() => setCrm(null))
            .finally(() => setLoading(false));
    }, [convId]);

    return { crm, loading };
}

/**
 * useSendAdminMessageV2 — enviar mensaje de texto o cualquier msg_type.
 *
 * @returns {{ send, sending, error }}
 *   send(convId, body, msgType?, payload?) → Promise<message>
 */
export function useSendAdminMessageV2() {
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const send = useCallback(
        async (convId, body, msgType = "text", payload = {}) => {
            setSending(true);
            setError(null);
            try {
                return await apiFetch(
                    `/api/v2/admin/live-chat/conversations/${convId}/messages`,
                    {
                        method: "POST",
                        body: JSON.stringify({ body, msg_type: msgType, payload }),
                    },
                );
            } catch (e) {
                setError(e.message);
                throw e;
            } finally {
                setSending(false);
            }
        },
        [],
    );

    return { send, sending, error };
}

/**
 * useSendRichCardV2 — enviar rich card predefinida.
 *
 * @returns {{ sendCard, sending }}
 *   sendCard(convId, cardType, data?) → Promise<message>
 */
export function useSendRichCardV2() {
    const [sending, setSending] = useState(false);

    const sendCard = useCallback(async (convId, cardType, data = {}) => {
        setSending(true);
        try {
            return await apiFetch(
                `/api/v2/admin/live-chat/conversations/${convId}/rich`,
                {
                    method: "POST",
                    body: JSON.stringify({ card_type: cardType, data }),
                },
            );
        } finally {
            setSending(false);
        }
    }, []);

    return { sendCard, sending };
}

/**
 * useMarkAdminReadV2 — marcar todos los mensajes del alumno como leídos.
 *
 * Llamar al abrir una conversación para resetear el badge de no leídos.
 *
 * @returns {Function} markRead(convId) → Promise<{ok, conversation_id}>
 */
export function useMarkAdminReadV2() {
    return useCallback(
        (convId) =>
            apiFetch(`/api/v2/admin/live-chat/conversations/${convId}/read`, {
                method: "PATCH",
            }),
        [],
    );
}

/**
 * useStartConversationV2 — inicia o recupera una conv con un alumno.
 *
 * Idempotente: si ya existe conv, la devuelve sin crear duplicado.
 *
 * @returns {{ start, loading }}
 *   start(studentId, initialMessage?) → Promise<conversation>
 */
export function useStartConversationV2() {
    const [loading, setLoading] = useState(false);

    const start = useCallback(async (studentId, initialMessage = null) => {
        setLoading(true);
        try {
            return await apiFetch("/api/v2/admin/live-chat/conversations", {
                method: "POST",
                body: JSON.stringify({
                    student_id: studentId,
                    initial_message: initialMessage,
                }),
            });
        } finally {
            setLoading(false);
        }
    }, []);

    return { start, loading };
}

/**
 * useChatStatsV2 — KPIs del hub para el badge de no leídos en el tab.
 *
 * Poll cada 8 segundos (menos frecuente — solo para el badge).
 *
 * @returns {{ data, loading, error, refetch }}
 *   data.total_conversations  → total de convs
 *   data.unread_conversations → convs con mensajes no leídos por admin
 *   data.total_messages       → total histórico de mensajes
 *   data.today_messages       → mensajes enviados hoy
 */
export function useChatStatsV2() {
    const fetcher = useCallback(
        () => apiFetch("/api/v2/admin/live-chat/stats"),
        [],
    );
    return usePoll(fetcher, 8000);
}

// ─────────────────────────────────────────────────────────────────────────────
//  ── STUDENT HOOKS ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

/**
 * useStudentConversationV2 — conversación del alumno autenticado.
 *
 * Se carga una sola vez al montar. La conversación es creada por el backend
 * si no existe (vía GET /api/v2/student/live-chat/conversation).
 *
 * @returns {{ conv, loading }}
 */
export function useStudentConversationV2() {
    const [conv, setConv] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiFetch("/api/v2/student/live-chat/conversation")
            .then(setConv)
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    return { conv, loading };
}

/**
 * useStudentMessagesV2 — mensajes de la conversación del alumno con polling incremental.
 *
 * [FIX P-06] lastId migrado de useState → useRef.
 *
 * ANTES (bug):
 *   const [lastId, setLastId] = useState(0);
 *   const fetcher = useCallback(async () => {
 *     ...
 *     setLastId(data.last_id); // ← state update
 *   }, [convId, lastId]);      // ← lastId en deps → fetcher se recrea
 *   usePoll(fetcher, ...);     // ← usePoll detecta nuevo fetcher → reinicia intervalo
 *                              // → al reiniciar, fetcher se ejecuta → setLastId →
 *                              //   fetcher se recrea → LOOP INFINITO
 *
 * AHORA (fix):
 *   const lastIdRef = useRef(0);
 *   const fetcher = useCallback(async () => {
 *     ...
 *     lastIdRef.current = data.last_id; // ← mutación de ref, SIN re-render
 *   }, [convId]);              // ← solo convId — estable entre polls
 *                              // → fetcher NUNCA se recrea entre polls
 *                              // → usePoll mantiene el mismo intervalo
 *
 * @param {number|null} convId  ID de la conversación del alumno
 * @returns {{ messages, unread, loading, clearUnread }}
 */
export function useStudentMessagesV2(convId) {
    const [messages, setMessages] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);

    // ← [FIX P-06] Ref en lugar de state — jamás en deps de useCallback
    const lastIdRef = useRef(0);

    const fetcher = useCallback(
        async () => {
            if (!convId) return false;
            let hubo = false;
            try {
                const data = await apiFetch(
                    `/api/v2/student/live-chat/conversation/messages` +
                    `?since_id=${lastIdRef.current}`, // ← lee el ref, no provoca re-render
                );
                if (data.messages?.length > 0) {
                    setMessages((prev) => {
                        const existingIds = new Set(prev.map((m) => m.id));
                        const newMsgs = data.messages.filter((m) => !existingIds.has(m.id));
                        return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
                    });
                    lastIdRef.current = data.last_id; // ← muta ref, SIN re-render → SIN loop
                    hubo = true;                      // ← el latido vuelve al ritmo vivo
                }
                if (data.unread_student !== undefined) {
                    setUnread(data.unread_student);
                }
            } finally {
                setLoading(false);
            }
            return hubo;
        },
        [convId], // ← SOLO convId — lastIdRef no es dep porque es un ref
    );

    // El chat del alumno late igual: rápido mientras hay conversación, más
    // lento si no llega nada y callado con la app en segundo plano. Es el que
    // más crece: cada alumno con la app abierta preguntaba 1.200 veces por hora.
    const acelera = useLatido(fetcher, { base: 3000, techo: 20000, activo: !!convId });

    const clearUnread = useCallback(() => setUnread(0), []);

    // `acelera` es para cuando el alumno acaba de escribir: la respuesta de
    // Spacy no puede esperar a que al latido le toque otra vez.
    return { messages, unread, loading, clearUnread, acelera };
}

/**
 * useSendStudentMessageV2 — el alumno envía un mensaje de texto.
 *
 * @returns {{ send, sending }}
 *   send(body) → Promise<message>
 */
export function useSendStudentMessageV2() {
    const [sending, setSending] = useState(false);

    const send = useCallback(async (body) => {
        setSending(true);
        try {
            return await apiFetch(
                "/api/v2/student/live-chat/conversation/messages",
                {
                    method: "POST",
                    body: JSON.stringify({ body }),
                },
            );
        } finally {
            setSending(false);
        }
    }, []);

    return { send, sending };
}

/**
 * useMarkStudentReadV2 — alumno marca sus mensajes como leídos.
 *
 * Llamar al abrir el chat para resetear el badge de no leídos del alumno.
 * Error silencioso — no romper UX si falla la marca.
 *
 * @returns {Function} markRead() → Promise<void>
 */
export function useMarkStudentReadV2() {
    return useCallback(
        () =>
            apiFetch("/api/v2/student/live-chat/conversation/read", {
                method: "PATCH",
            }).catch(() => { }),
        [],
    );
}

/**
 * useQuickReplyV2 — alumno responde a una quick_reply card.
 *
 * @returns {Function} quickReply(replyToId, selected) → Promise<message>
 */
export function useQuickReplyV2() {
    return useCallback(
        (replyToId, selected) =>
            apiFetch("/api/v2/student/live-chat/conversation/quick-reply", {
                method: "POST",
                body: JSON.stringify({ reply_to_id: replyToId, selected }),
            }),
        [],
    );
}

/**
 * useAllStudentsV2 — lista TODOS los alumnos para el selector de nueva conv.
 *
 * Poll cada 15s (baja frecuencia — la lista de alumnos cambia poco).
 *
 * @param {string} search  Texto de búsqueda (debounceado en el componente)
 * @returns {{ data, loading, error }}
 */
export function useAllStudentsV2(search = "") {
    const fetcher = useCallback(
        () =>
            apiFetch(
                `/api/v2/admin/live-chat/students` +
                `?search=${encodeURIComponent(search)}&limit=200`,
            ),
        [search],
    );
    return usePoll(fetcher, 15000, [search]);
}

// ════════════════════════════════════════════════════════════════════════════
//  Hooks de borrado — Admin y Student
// ════════════════════════════════════════════════════════════════════════════

/**
 * useDeleteAdminMessageV2 — Admin borra un mensaje individual.
 *
 * El backend verifica que el msg_id pertenezca al conv_id para
 * prevenir borrado cross-conversation.
 *
 * @returns {{ deleteMsg, deleting }}
 *   deleteMsg(convId, msgId) → Promise<{ok, deleted_id, conv_id}>
 */
export function useDeleteAdminMessageV2() {
    const [deleting, setDeleting] = useState(false);

    const deleteMsg = useCallback(async (convId, msgId) => {
        setDeleting(true);
        try {
            return await apiFetch(
                `/api/v2/admin/live-chat/conversations/${convId}/messages/${msgId}`,
                { method: "DELETE" },
            );
        } finally {
            setDeleting(false);
        }
    }, []);

    return { deleteMsg, deleting };
}

/**
 * useDeleteConversationV2 — Admin borra la conversación entera.
 *
 * Borra mensajes + cabecera de conversación. El alumno (User) NO se borra.
 * Operación irreversible — solicitar confirmación en el componente.
 *
 * @returns {{ deleteConv, deleting }}
 *   deleteConv(convId) → Promise<{ok, conv_id, deleted_msgs}>
 */
export function useDeleteConversationV2() {
    const [deleting, setDeleting] = useState(false);

    const deleteConv = useCallback(async (convId) => {
        setDeleting(true);
        try {
            return await apiFetch(
                `/api/v2/admin/live-chat/conversations/${convId}`,
                { method: "DELETE" },
            );
        } finally {
            setDeleting(false);
        }
    }, []);

    return { deleteConv, deleting };
}

/**
 * useDeleteStudentMessageV2 — El alumno borra uno de sus propios mensajes.
 *
 * El backend verifica que el mensaje pertenezca al alumno autenticado
 * y que sea de tipo "text" (no se pueden borrar rich cards recibidas).
 *
 * @returns {{ deleteMsg, deleting }}
 *   deleteMsg(msgId) → Promise<{ok, deleted_id}>
 */
export function useDeleteStudentMessageV2() {
    const [deleting, setDeleting] = useState(false);

    const deleteMsg = useCallback(async (msgId) => {
        setDeleting(true);
        try {
            return await apiFetch(
                `/api/v2/student/live-chat/conversation/messages/${msgId}`,
                { method: "DELETE" },
            );
        } finally {
            setDeleting(false);
        }
    }, []);

    return { deleteMsg, deleting };
}